import { useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { ACTIONS, EVENTS, SCREENS } from '../../../constants/tracking';
import { useApp } from '../../../contexts/AppContext';
import { useTracking } from '../../../contexts/TrackingContext';
import { useTurnOnEma } from '../../../hooks/mutations/useTurnOnEma';
import { isValidHttpsUrl } from '../../../utils/urls';
import { EnterpriseManagedAuthAccountId } from './EnterpriseManagedAuthAccountId';
import { EnterpriseManagedAuthPanel } from './EnterpriseManagedAuthPanel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function EnterpriseManagedAuthSetup({
  accountId,
  onCancel
}) {
  const [issuerUrl, setIssuerUrl] = useState('');
  const {
    turnOnEma,
    loading,
    error
  } = useTurnOnEma();
  const {
    appId,
    appName
  } = useApp();
  const {
    track
  } = useTracking();
  const trimmedIssuerUrl = issuerUrl.trim();
  const hasIssuerUrlError = trimmedIssuerUrl !== '' && !isValidHttpsUrl(trimmedIssuerUrl);
  const handleTurnOn = () => void turnOnEma(trimmedIssuerUrl).then(({
    data
  }) => {
    if (data) {
      track(EVENTS.PANEL_INTERACTED, {
        screen: SCREENS.MANAGEMENT_MODAL_PANEL,
        action: ACTIONS.EMA_TURNED_ON,
        appId,
        appName
      });
    }
  });
  const handleCancel = () => {
    track(EVENTS.PANEL_INTERACTED, {
      screen: SCREENS.MANAGEMENT_MODAL_PANEL,
      action: ACTIONS.EMA_SETUP_CANCELLED,
      appId,
      appName
    });
    onCancel();
  };
  return /*#__PURE__*/_jsxs(EnterpriseManagedAuthPanel, {
    statusTagUse: "inReview",
    statusLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.advanced.ema.setup.status"
    }),
    children: [/*#__PURE__*/_jsx(Text, {
      as: "div",
      typography: "heading-50",
      children: /*#__PURE__*/_jsxs(List, {
        ordered: true,
        styled: true,
        listStylePosition: "inside",
        paddingInlineStart: "0",
        children: [/*#__PURE__*/_jsxs(ListItem, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.advanced.ema.setup.accountId.step"
          }), /*#__PURE__*/_jsx(Text, {
            as: "div",
            typography: "body-100",
            children: /*#__PURE__*/_jsx(EnterpriseManagedAuthAccountId, {
              value: accountId,
              label: I18n.text('app-governance-lib.advanced.ema.setup.accountId.label'),
              helpText: I18n.text('app-governance-lib.advanced.ema.setup.accountId.help'),
              hasBottomMargin: true
            })
          })]
        }), /*#__PURE__*/_jsxs(ListItem, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.advanced.ema.setup.issuerUrl.step"
          }), /*#__PURE__*/_jsx(Text, {
            as: "div",
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormControl, {
              label: I18n.text('app-governance-lib.advanced.ema.setup.issuerUrl.label'),
              required: true,
              help: I18n.text('app-governance-lib.advanced.ema.setup.issuerUrl.help'),
              error: hasIssuerUrlError,
              validationMessage: hasIssuerUrlError ? I18n.text('app-governance-lib.advanced.ema.setup.issuerUrl.invalid') : undefined,
              children: /*#__PURE__*/_jsx(TextInput, {
                value: issuerUrl,
                onChange: e => setIssuerUrl(e.target.value),
                placeholder: I18n.text('app-governance-lib.advanced.ema.setup.issuerUrl.placeholder')
              })
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(Alert, {
      type: "warning",
      titleText: I18n.text('app-governance-lib.advanced.ema.setup.alert.title'),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.advanced.ema.setup.alert.description"
      })
    }), /*#__PURE__*/_jsxs(ButtonWrapper, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        use: "primary",
        loading: loading,
        failed: Boolean(error),
        disabled: trimmedIssuerUrl === '' || hasIssuerUrlError,
        onClick: handleTurnOn,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.advanced.ema.setup.turnOn"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: handleCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.advanced.ema.setup.cancel"
        })
      })]
    })]
  });
}